/**
 * usePersistentCollapse — reusable collapse state that survives reloads and
 * navigations, persisted to localStorage.
 *
 * Lifted from LeftNav's `l4e_leftnav_collapsed` pattern (the only place in
 * the app that persisted collapse before this) so every Rail can share one
 * behavior instead of re-forking it. See POLICY_NAVIGATION_RAILS.md §3 and
 * ANALYSIS_LEFT_RAILS.md ("collapse persistence exists in exactly one
 * place").
 *
 * Guards every localStorage access in try/catch — SSR, privacy mode, and
 * storage-disabled browsers must degrade to in-memory state, never throw.
 */
import { useCallback, useEffect, useState } from 'react';

export interface UsePersistentCollapseResult {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;
}

/**
 * @param persistKey  localStorage key (e.g. 'l4e_rail_communities_collapsed').
 *                    When omitted/empty the state is in-memory only.
 * @param defaultCollapsed  Initial value when nothing is stored yet
 *                          (e.g. Discover's Activity Rail starts collapsed).
 */
export function usePersistentCollapse(
  persistKey?: string,
  defaultCollapsed = false,
): UsePersistentCollapseResult {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (!persistKey) return defaultCollapsed;
    try {
      const stored = window?.localStorage?.getItem(persistKey);
      if (stored === null || stored === undefined) return defaultCollapsed;
      return stored === 'true';
    } catch {
      return defaultCollapsed;
    }
  });

  useEffect(() => {
    if (!persistKey) return;
    try {
      window?.localStorage?.setItem(persistKey, String(collapsed));
    } catch {
      /* ignore — privacy mode / storage disabled */
    }
  }, [persistKey, collapsed]);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((prev) => !prev);
  }, []);

  return { collapsed, setCollapsed, toggleCollapsed };
}

export default usePersistentCollapse;
